/**
 * Custom Bundle / mix-and-match set — Vue 3 (vue.global.prod.js).
 * Builder state in the sidebar; one cart/add.js call with all component lines
 * (same as build-your-routine: _bundle_group_id, _bundle_parent_variant_id, _bundle_slot).
 * Depends: window.BundleCartHelpers, window.Vue, in-DOM template, [[ ]] delimiters.
 */
(function (global) {
  'use strict';

  const newLocalId = () =>
    `L-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  const variantPriceToCents = (v) => {
    if (!v || v.price == null) return 0;
    const p = v.price;
    if (typeof p === 'number') return Math.round(p);
    const s = String(p).trim();
    if (s.includes('.')) return Math.round(parseFloat(s) * 100);
    const n = parseInt(s, 10);
    return Number.isNaN(n) ? 0 : n;
  };

  const defaultDiscountPercent = (count, tiers) => {
    if (!Array.isArray(tiers) || !count) return 0;
    let out = 0;
    for (const row of tiers) {
      if (!row) continue;
      const min = Math.max(0, Number(row.min) || 0);
      const percent = Math.min(100, Math.max(0, Number(row.percent) || 0));
      if (count >= min && percent > out) out = percent;
    }
    return out;
  };

  const pickVariant = (p, pickMap) => {
    if (!p?.variants?.length) return null;
    const fromMap = pickMap[p.id];
    if (fromMap) {
      const m = p.variants.find((v) => String(v.id) === String(fromMap));
      if (m) return m;
    }
    const firstAvail = p.variants.find((v) => v.available);
    return firstAvail || p.variants[0] || null;
  };

  const storageKey = (parentNumeric) =>
    `riversol_custom_bundle_group_${String(parentNumeric)}`;

  /**
   * One key per (sectionId, tierIndex) pair so the same merchant
   * can run multiple bundle-set sections in parallel (different
   * collections / different parent variants) without the picks
   * bleeding into each other.
   */
  const rewardPickStorageKey = (sectionId, tierIndex) =>
    `riversol_custom_bundle_reward_pick_${String(sectionId)}_t${String(tierIndex)}`;

  const readPickedRewardVariantId = (sectionId, tierIndex) => {
    try {
      const raw = window.localStorage.getItem(
        rewardPickStorageKey(sectionId, tierIndex)
      );
      if (!raw) return null;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    } catch {
      return null;
    }
  };

  const writePickedRewardVariantId = (sectionId, tierIndex, variantId) => {
    try {
      const k = rewardPickStorageKey(sectionId, tierIndex);
      if (variantId == null) window.localStorage.removeItem(k);
      else window.localStorage.setItem(k, String(Number(variantId)));
    } catch {
      /* noop */
    }
  };

  /**
   * Resolve which variant of `tier.productId` we should currently
   * use for the auto-add. Order of preference:
   *   1. Single-variant reward                      -> tier.defaultVariantId
   *   2. Multi-variant + valid stored pick          -> stored id
   *   3. Multi-variant fallback (first available)   -> first available
   *   4. None available                             -> null
   */
  const resolveRewardVariantId = (tier, sectionId) => {
    if (!tier) return null;
    if (!tier.hasMultiVariants) return tier.defaultVariantId || null;
    const picked = readPickedRewardVariantId(sectionId, tier.tierIndex);
    if (picked) {
      const found = tier.variants.find((v) => v.id === picked);
      if (found && found.available) return picked;
    }
    const firstAvail = tier.variants.find((v) => v.available);
    if (firstAvail) return firstAvail.id;
    return tier.defaultVariantId || null;
  };

  global.mountCustomBundleSetSection = function mountCustomBundleSetSection(
    rawCfg
  ) {
    if (!global.Vue?.createApp) {
      console.warn('[CustomBundleSet] Vue not found; skipping mount.');
      return;
    }
    const H = global.BundleCartHelpers;
    if (!H) {
      console.warn('[CustomBundleSet] BundleCartHelpers missing; skipping mount.');
      return;
    }

    const cfg = rawCfg || {};
    const sectionId = cfg.sectionId;
    if (sectionId == null || sectionId === '') return;
    const rootEl = document.getElementById(`CustomBundleSetApp-${sectionId}`);
    if (!rootEl) return;

    const parentVariantNumeric = Number(cfg.bundleParentVariantId);
    const parentGid = H.parentVariantGid(parentVariantNumeric);
    const rawBundleGroupId = cfg.bundleGroupId;
    const BUNDLE_GROUP_ID = String(
      rawBundleGroupId && String(rawBundleGroupId).trim() !== ''
        ? String(rawBundleGroupId).trim()
        : 'bundle-and-save'
    );
    const products = Array.isArray(cfg.products) ? cfg.products : [];
    const discountTiers = Array.isArray(cfg.discountTiers)
      ? cfg.discountTiers
      : [];
    /**
     * Reward tiers: a normalized projection of `discountTiers` that
     * keeps only the tiers with a valid free-gift product attached.
     * The sidebar gift list and the cart commit step both iterate
     * this array, so any tier without a reward is invisible to the
     * gift code path.
     *
     * Multi-variant rewards are normalized to a `variants` array of
     * `{ id, title, available }` so the sidebar `<select>` can render
     * them directly without re-fetching /products/<handle>.js.
     */
    const rewardTiers = [];
    discountTiers.forEach((row, idx) => {
      if (!row) return;
      const min = Math.max(0, Number(row.min) || 0);
      const percent = Math.min(100, Math.max(0, Number(row.percent) || 0));
      const hasReward = Boolean(row.reward_product_id && row.reward_variant_id);
      if (min <= 0 || (percent <= 0 && !hasReward)) return;
      const productId = row.reward_product_id;
      const variantId = row.reward_variant_id;
      if (!productId || !variantId) return;
      const variants = Array.isArray(row.reward_variants)
        ? row.reward_variants
            .filter((v) => v && v.id != null)
            .map((v) => ({
              id: Number(v.id),
              title: String(v.title || '').trim(),
              available: Boolean(v.available),
            }))
        : [];
      const optionNames = Array.isArray(row.reward_option_names)
        ? row.reward_option_names
            .map((n) => {
              if (n == null) return '';
              if (typeof n === 'object' && n.name != null) {
                return String(n.name).trim();
              }
              return String(n).trim();
            })
            .filter(Boolean)
        : [];
      rewardTiers.push({
        tierIndex: row.tier_index != null ? Number(row.tier_index) : idx + 1,
        min,
        percent,
        productId: Number(productId),
        defaultVariantId: Number(variantId),
        variantTitle: String(row.reward_variant_title || '').trim(),
        productTitle: String(row.reward_product_title || '').trim(),
        image: String(row.reward_image || '').trim(),
        hasMultiVariants: Boolean(row.reward_has_multi_variants),
        optionNames,
        variants,
      });
    });
    const minLineItems = Math.max(1, Number(cfg.minLineItems) || 1);
    const soldOutLabel = cfg.soldOutLabel || 'Sold out';
    const addProductToBuilderLabel =
      cfg.addProductToBuilderLabel || 'Add';
    /**
     * When true, products whose first option is named "Size"
     * (case-insensitive) skip the variant `<select>` in the product
     * grid. The + control still works because `pickVariant` falls back to
     * the first available variant when no explicit pick exists.
     * Driven by the `hide_size_variant_selector` schema setting.
     */
    const hideSizeVariantSelector = cfg.hideSizeVariantSelector === true;
    const addToSetCtaLabel = cfg.addToSetCtaLabel || 'Add to set';
    const updateSetCtaLabel = cfg.updateSetCtaLabel || 'Update the set';
    // CTA copy when the customer locally emptied the bundle but a
    // bundle is still in the cart — clicking commits the "remove
    // the bundle" branch of commitBundleToCart.
    const removeSetCtaLabel = cfg.removeSetCtaLabel || 'Remove the set';
    const startSetCtaLabel = cfg.startSetCtaLabel || 'Pick your first item';
    const removeFromSetLabel = cfg.removeFromSetLabel || 'Remove';
    const emptySetLabel = cfg.emptySetLabel || 'No items in your set yet.';
    const subtotalLabel = cfg.subtotalLabel || 'Subtotal';
    const saleLabel = cfg.saleLabel || 'Sale';
    const sidebarSlotCount = Math.min(
      12,
      Math.max(4, parseInt(String(cfg.sidebarSlotCount || 6), 10) || 6)
    );
    const selectSlotLabel = cfg.selectSlotLabel || 'Select an item';
    const sidebarPromoText = (cfg.sidebarPromoText || '').trim();
    const sidebarPromoImage = (cfg.sidebarPromoImage || '').trim();
    const showSidebarPromo = Boolean(
      (sidebarPromoText && sidebarPromoText.length > 0) ||
        (sidebarPromoImage && sidebarPromoImage.length > 0)
    );
    const { createApp, ref, computed, onMounted, onUnmounted, watch, nextTick } =
      global.Vue;

    const addUrl =
      global.Shopify?.routes?.root != null
        ? `${global.Shopify.routes.root}cart/add.js`
        : '/cart/add.js';

    const findVariantInProducts = (prods, variantId) => {
      const vid = String(variantId);
      for (const p of prods) {
        if (!p?.variants?.length) continue;
        const v = p.variants.find((x) => String(x.id) === vid);
        if (v) return { product: p, variant: v };
      }
      return { product: null, variant: null };
    };

    /**
     * Push one sidebar line for a bundle component variant. Tier reward
     * SKUs may match components the customer added on purpose — parent /
     * item_components metadata is authoritative, so we never filter by
     * reward tier config here.
     */
    const appendHydratedLineForVariant = (out, vid, qty, prods) => {
      const q = Math.max(1, parseInt(String(qty), 10) || 1);
      const { product: p, variant: v } = findVariantInProducts(prods, vid);
      if (v) {
        const vFeat =
          v.featured_image != null ? String(v.featured_image).trim() : '';
        const pFeat =
          p?.featured_image != null ? String(p.featured_image).trim() : '';
        const imageSrc = vFeat || pFeat;
        const vTitle = v.title ? String(v.title).trim() : '';
        const displayVariant =
          vTitle && vTitle !== 'Default Title' ? vTitle : '';
        out.push({
          _fromParentBundle: true,
          _localId: newLocalId(),
          product_id: p?.id,
          productId: p?.id,
          title: p?.title || 'Item',
          variant_id: v.id,
          variantId: v.id,
          variant_title: displayVariant,
          image: imageSrc,
          priceCents: variantPriceToCents(v),
          quantity: q,
        });
      } else {
        out.push({
          _fromParentBundle: true,
          _localId: newLocalId(),
          product_id: null,
          productId: null,
          title: 'Item',
          variant_id: vid,
          variantId: vid,
          variant_title: '',
          image: '',
          priceCents: 0,
          quantity: q,
        });
      }
    };

    /**
     * When Liquid has `item_components` on the bundle parent, prefer it
     * (same units as the storefront cart). Ajax /cart.js often omits this
     * field; when present it is the most accurate source for per-component
     * quantities.
     */
    const buildHydratedLinesFromItemComponents = (parentItem, prods) => {
      const comps = parentItem?.item_components || parentItem?.items;
      if (!Array.isArray(comps) || comps.length === 0) return null;
      const out = [];
      for (const comp of comps) {
        const rawVid = comp.variant_id ?? comp.variantId ?? comp.id;
        const vid = Number(rawVid);
        if (Number.isNaN(vid)) continue;
        const qty = comp.quantity != null ? comp.quantity : 1;
        appendHydratedLineForVariant(out, vid, qty, prods);
      }
      return out.length ? out : null;
    };

    const buildHydratedLinesFromParent = (
      variantIds,
      quantities,
      prods,
      parentItem
    ) => {
      const ids = variantIds.slice();
      let qs = (quantities || []).slice();
      while (qs.length < ids.length) {
        qs.push(1);
      }

      if (ids.length === 1 && parentItem) {
        const hintRaw = H.getProp(parentItem, '_custom_bundle_items');
        const hint =
          hintRaw != null && String(hintRaw).trim() !== ''
            ? Math.max(1, parseInt(String(hintRaw), 10) || 0)
            : 0;
        const baseQ = Math.max(1, parseInt(String(qs[0] ?? 1), 10) || 1);
        if (hint > baseQ) qs[0] = hint;
        const rawGid = H.getProp(parentItem, '_bundle_component_variant_ids');
        if (typeof rawGid === 'string' && rawGid.indexOf('ProductVariant/') !== -1) {
          const occ = (rawGid.match(/ProductVariant\//gi) || []).length;
          if (occ > 1) qs[0] = Math.max(qs[0], occ);
        }
      }

      const out = [];
      for (let i = 0; i < ids.length; i += 1) {
        appendHydratedLineForVariant(out, ids[i], qs[i], prods);
      }
      return out;
    };

    const makeLocalLine = (p, v, imageSrc) => {
      const vTitle = v.title
        ? String(v.title).trim()
        : '';
      const displayVariant =
        vTitle && vTitle !== 'Default Title' ? vTitle : '';
      return {
        _localId: newLocalId(),
        product_id: p.id,
        productId: p.id,
        title: p.title,
        variant_id: v.id,
        variantId: v.id,
        variant_title: displayVariant,
        image: imageSrc || p.featured_image || '',
        priceCents: variantPriceToCents(v),
        quantity: 1,
      };
    };

    /**
     * Mobile (<1024px): mirror the fixed aside height onto the section as
     * `--cbs-mobile-panel-h` so list content clears the bar. When the bar
     * is off-screen (no `is-mobile-bar-revealed` on `<aside>`), spacer is 0.
     */
    const sectionEl = rootEl.closest('.custom-bundle-set');
    const setMobilePanelSpacer = (px) => {
      if (!sectionEl) return;
      sectionEl.style.setProperty(
        '--cbs-mobile-panel-h',
        `${Math.max(0, Math.round(px || 0))}px`
      );
    };
    const MOBILE_FLOATING_BAR_MQ = '(max-width: 1023.98px)';
    const getWindowScrollY = () =>
      Math.max(
        window.pageYOffset || 0,
        window.scrollY || 0,
        document.documentElement ? document.documentElement.scrollTop || 0 : 0,
        document.body ? document.body.scrollTop || 0 : 0,
        document.scrollingElement ? document.scrollingElement.scrollTop || 0 : 0
      );
    const measureMobileFloatingBar = () => {
      if (!sectionEl) {
        return;
      }
      const aside = sectionEl.querySelector('.custom-bundle-set__side');
      if (!aside) {
        setMobilePanelSpacer(0);
        return;
      }
      if (window.matchMedia(MOBILE_FLOATING_BAR_MQ).matches) {
        if (!aside.classList.contains('is-mobile-bar-revealed')) {
          setMobilePanelSpacer(0);
          return;
        }
      }
      setMobilePanelSpacer(aside.getBoundingClientRect().height);
    };

    const app = createApp({
      name: 'CustomBundleSetSection',
      delimiters: ['[[', ']]'],
      setup() {
        const lines = ref([]);
        const loading = ref(true);
        const actionKey = ref(null);
        const variantPick = ref({});
        const productJson = ref({});
        const stableBundleGroupId = ref(null);
        const hasBundleInCart = ref(false);
        /**
         * Mobile floating-panel open/closed state. The sidebar
         * `<aside>` collapses to a slim handle on small viewports
         * (see CSS `.custom-bundle-set__side` mobile media query).
         * Default: closed so the panel doesn't eat half the viewport
         * when the page first loads. Customer toggles via the handle.
         * Desktop ignores this value entirely.
         */
        const mobilePanelOpen = ref(false);
        const toggleMobilePanel = () => {
          mobilePanelOpen.value = !mobilePanelOpen.value;
        };
        /**
         * Mobile <1024px: hide the fixed `<aside>` until the page has been
         * scrolled (`scrollTop > 0`). At `scrollTop === 0` it stays off-screen.
         * Desktop: always treated as “revealed” for layout/spacer math.
         */
        const mobileBarRevealed = ref(
          typeof window === 'undefined' ||
            !window.matchMedia(MOBILE_FLOATING_BAR_MQ).matches
        );

        let mobileRevealTeardown = () => {};

        const syncMobileBarFromWindowScroll = () => {
          const mq = window.matchMedia(MOBILE_FLOATING_BAR_MQ);
          if (!mq.matches) {
            mobileBarRevealed.value = true;
            return;
          }
          mobileBarRevealed.value = getWindowScrollY() > 0;
        };

        watch(mobileBarRevealed, () => {
          nextTick(() => measureMobileFloatingBar());
        });
        /**
         * Map of `tierIndex -> picked variant id` for multi-variant
         * rewards. Seeded from localStorage at mount time so the
         * customer's last pick survives a reload, then mutated via
         * `setRewardVariantPick` whenever they change the gift's
         * variant in the sidebar `<select>`.
         */
        const rewardVariantPicks = ref({});
        rewardTiers.forEach((tier) => {
          if (!tier.hasMultiVariants) return;
          const stored = readPickedRewardVariantId(sectionId, tier.tierIndex);
          if (stored) rewardVariantPicks.value[tier.tierIndex] = stored;
        });

        const lineCentsRaw = (line) => {
          if (line._fromParentBundle) {
            const unit = line.priceCents != null ? line.priceCents : 0;
            const q = Math.max(
              1,
              parseInt(String(line.quantity || 1), 10) || 1
            );
            return unit * q;
          }
          if (line._localId) {
            const unit = line.priceCents != null ? line.priceCents : 0;
            const q = Math.max(
              1,
              parseInt(String(line.quantity || 1), 10) || 1
            );
            return unit * q;
          }
          const p = line?.final_line_price;
          if (p != null) return p;
          return line?.line_price || 0;
        };

        const getLineUnitCount = (line) => {
          if (!line) return 0;
          if (line.quantity == null) return 1;
          const q = parseInt(String(line.quantity), 10);
          if (Number.isNaN(q) || q < 1) return 1;
          return q;
        };

        const lineCentsPerUnit = (line) => {
          if (!line) return 0;
          if (line._fromParentBundle || line._localId) {
            return line.priceCents != null ? line.priceCents : 0;
          }
          const q = getLineUnitCount(line);
          if (q < 1) return 0;
          return Math.round((lineCentsRaw(line) / q) || 0);
        };

        const totalUnitCount = computed(() =>
          lines.value.reduce((s, l) => s + getLineUnitCount(l), 0)
        );
        const lineCount = totalUnitCount;
        /**
         * Short status label rendered inside the mobile floating-
         * panel handle (e.g. "0 products chosen", "3 products
         * chosen"). Plural-aware. Only ever shown on small viewports
         * via CSS, but the value is computed unconditionally so the
         * template doesn't have to care which breakpoint it's in.
         */
        const mobileChosenLabel = computed(() => {
          const n = totalUnitCount.value;
          return `${n} ${n === 1 ? 'product' : 'products'} chosen`;
        });
        /**
         * Whether the CTA can be clicked. Two valid commit shapes:
         *   1. The local set has at least `minLineItems` units
         *      (normal "add a bundle" / "update a bundle" path).
         *   2. The customer has emptied the local set AND a bundle
         *      is already in the cart — clicking the CTA in this
         *      state issues the "remove the bundle" branch of
         *      `commitBundleToCart`. Without this branch, removing
         *      every item in the builder would strand the customer
         *      with no way to commit the change.
         */
        const canAddBundle = computed(() => {
          if (totalUnitCount.value >= minLineItems) return true;
          if (totalUnitCount.value === 0 && hasBundleInCart.value) return true;
          return false;
        });

        /**
         * Empty builder and no bundle in the cart: instead of a greyed-out
         * "Add to cart", the (still disabled) CTA shows a plain
         * "Pick your first item" prompt.
         */
        const isStartState = computed(() => totalUnitCount.value === 0 && !hasBundleInCart.value);

        const bundleCtaLabel = computed(() => {
          if (isStartState.value) return startSetCtaLabel;
          if (hasBundleInCart.value && totalUnitCount.value === 0) {
            return removeSetCtaLabel;
          }
          return hasBundleInCart.value ? updateSetCtaLabel : addToSetCtaLabel;
        });

        const sumCents = computed(() => {
          let t = 0;
          for (const line of lines.value) {
            t += lineCentsRaw(line);
          }
          return t;
        });
        const discountPercent = computed(() =>
          defaultDiscountPercent(totalUnitCount.value, discountTiers)
        );
        const totalDiscountedCents = computed(() => {
          const o = sumCents.value;
          const f = 1 - discountPercent.value / 100;
          return Math.round(o * f);
        });
        const hasDiscount = computed(
          () => totalUnitCount.value > 0 && discountPercent.value > 0
        );

        /**
         * Ordered discount gates (unique `min`, highest `percent` wins if
         * duplicates). Drives the sidebar countdown until every configured
         * tier threshold is reached.
         */
        const sortedDiscountGates = computed(() => {
          if (!Array.isArray(discountTiers) || !discountTiers.length) {
            return [];
          }
          const map = new Map();
          for (const row of discountTiers) {
            if (!row) continue;
            const min = Math.max(0, Number(row.min) || 0);
            const percent = Math.min(100, Math.max(0, Number(row.percent) || 0));
            if (min <= 0 || percent <= 0) continue;
            const prev = map.get(min) ?? 0;
            if (percent > prev) map.set(min, percent);
          }
          return [...map.entries()]
            .map(([min, percent]) => ({ min, percent }))
            .sort((a, b) => a.min - b.min);
        });

        /**
         * Next milestone: either the upcoming discount tier or (when no
         * discount tiers exist) the section `minLineItems` floor.
         */
        const nextTierProgress = computed(() => {
          const count = totalUnitCount.value;
          const gates = sortedDiscountGates.value;
          if (gates.length) {
            const next = gates.find((g) => count < g.min);
            if (next) {
              const need = next.min - count;
              if (need > 0) {
                return {
                  kind: 'discount',
                  need,
                  percent: next.percent,
                  gateMin: next.min,
                };
              }
            }
          }
          if (count < minLineItems) {
            return { kind: 'min', need: minLineItems - count };
          }
          return null;
        });

        /** Discount gate whose `min` equals the current line count (just unlocked). */
        const discountTierGateExactHit = computed(() => {
          const count = totalUnitCount.value;
          if (count <= 0) return null;
          const gates = sortedDiscountGates.value;
          for (let i = 0; i < gates.length; i += 1) {
            const g = gates[i];
            if (g.min === count) return g;
          }
          for (const tier of rewardTiers) {
            if (tier.percent <= 0 && tier.min === count) {
              return { min: tier.min, percent: 0, isGiftOnly: true };
            }
          }
          return null;
        });

        const showTierProgressHint = computed(() => {
          if (totalUnitCount.value === 0 && hasBundleInCart.value) {
            return false;
          }
          if (discountTierGateExactHit.value != null) return true;
          return nextTierProgress.value != null;
        });

        const tierProgressHintText = computed(() => {
          const hit = discountTierGateExactHit.value;
          if (hit) {
            if (hit.isGiftOnly) {
            return `You've unlocked your free gift!`;
          }
            const gift = rewardTiers.find((t) => t.min === hit.min);
            const suffix = gift ? ` + gift!` : `!`;
            const unlockedText = `You unlocked <b>${hit.percent}% off</b>${suffix}`;
            // Point at the next reward: a free gift still ahead wins over a bigger discount
            const nextGift = rewardTiers.find((t) => t.min > hit.min);
            const nextGate = sortedDiscountGates.value.find((g) => g.min > hit.min);
            const next = nextGift || nextGate;
            if (!next) return unlockedText;
            const need = next.min - hit.min;
            const reward = nextGift ? 'a free gift' : `<b>${nextGate.percent}% off</b>`;
            return `${unlockedText} <b>${need} more</b> for ${reward}.`;
          }
          const p = nextTierProgress.value;
          if (!p) return '';
          const u = p.need === 1 ? 'product' : 'products';
          if (p.kind === 'discount') {
            const gift = rewardTiers.find((t) => t.min === p.gateMin);
            let s = `Add <b>${p.need} more</b> ${u} to unlock <b>${p.percent}% off</b>`;
            if (gift) s += ' and your free gift';
            return `${s}.`;
          }
          if (p.kind === 'gift') {
            return `Add <b>${p.need} more</b> ${u} to unlock your free gift.`;
          }
          return `Add <b>${p.need} more</b> ${u} to add or update your set.`;
        });

        /**
         * The sidebar's "Your free gifts" list. One row per configured
         * reward tier (in tier-index order). Each row is annotated
         * with the runtime lock state, the resolved variant id, and a
         * display payload for the `<select>` and title rows.
         */
        const rewardTierRows = computed(() => {
          if (!rewardTiers.length) return [];
          const count = totalUnitCount.value;
          return rewardTiers.map((tier) => {
            const unlocked = count >= tier.min;
            const pickedId = rewardVariantPicks.value[tier.tierIndex] || null;
            const selectedId =
              tier.hasMultiVariants && pickedId
                ? pickedId
                : resolveRewardVariantId(tier, sectionId) ||
                  tier.defaultVariantId;
            const selectedVariant =
              tier.variants.find((v) => v.id === selectedId) || null;
            const variantLabel =
              selectedVariant && selectedVariant.title
                ? selectedVariant.title
                : tier.variantTitle || '';
            const displayVariantTitle =
              variantLabel && variantLabel !== 'Default Title'
                ? variantLabel
                : '';
            return {
              tierIndex: tier.tierIndex,
              min: tier.min,
              percent: tier.percent,
              productTitle: tier.productTitle,
              image: tier.image,
              hasMultiVariants: tier.hasMultiVariants,
              variants: tier.variants,
              optionName: tier.optionNames[0] || '',
              selectedVariantId: selectedId,
              displayVariantTitle,
              unlocked,
            };
          });
        });
        const hasRewardTiers = computed(() => rewardTierRows.value.length > 0);

        /**
         * One entry per **unit** in the sidebar (same variant × N → N visual slots).
         * /cart/add format is chosen in commitBundleToCart.
         */
        const sidebarDisplayRows = computed(() => {
          const rows = [];
          for (const line of lines.value) {
            const q = getLineUnitCount(line);
            const base = line._localId
              ? String(line._localId)
              : line.key
                ? String(line.key)
                : String(line.id != null ? line.id : rows.length);
            for (let u = 0; u < q; u += 1) {
              rows.push({
                key: `${base}-u${u}`,
                line,
              });
            }
          }
          return rows;
        });

        /**
         * Visual slot strip: product cells numbered 1..N in order, with
         * each free-gift tier inserted *before* the product cell that
         * matches its `min` (e.g. gift-at-3 → before product #3). Gift
         * cells do not consume a product number, so numbering stays
         * 1, 2, gift, 3, 4, 5…
         */
        const displaySlots = computed(() => {
          const productCount = Math.max(
            sidebarSlotCount,
            sidebarDisplayRows.value.length,
            1
          );
          const giftsByMin = new Map();
          for (const row of rewardTierRows.value) {
            const min = Math.max(0, Number(row.min) || 0);
            if (min <= 0) continue;
            const list = giftsByMin.get(min) || [];
            list.push(row);
            giftsByMin.set(min, list);
          }
          const cells = [];
          for (let p = 0; p < productCount; p += 1) {
            const giftsHere = giftsByMin.get(p + 1);
            if (giftsHere) {
              for (const gift of giftsHere) {
                cells.push({
                  kind: 'gift',
                  productIndex: -1,
                  displayNumber: null,
                  tierIndex: gift.tierIndex,
                  key: `gift-${gift.tierIndex}`,
                });
              }
            }
            cells.push({
              kind: 'product',
              productIndex: p,
              displayNumber: p + 1,
              key: `product-${p}`,
            });
          }
          return cells;
        });

        const isGiftSlot = (slot) => Boolean(slot && slot.kind === 'gift');

        /**
         * Reward-tier row for an inserted gift cell, or null.
         */
        const giftRewardForSlot = (slot) => {
          if (!isGiftSlot(slot)) return null;
          return (
            rewardTierRows.value.find(
              (row) => row.tierIndex === slot.tierIndex
            ) || null
          );
        };

        const setVariantPick = (productId, variantId) => {
          variantPick.value = {
            ...variantPick.value,
            [productId]: variantId,
          };
        };

        const productVariantList = (p) => {
          if (p?.handle) {
            const fromJson = productJson.value[p.handle];
            if (fromJson?.variants?.length) return fromJson.variants;
          }
          return p?.variants?.length ? p.variants : [];
        };

        /**
         * Read the product's option1 name (e.g. "Size", "Color").
         * Prefers the freshly-fetched products.json payload (which
         * always reflects the merchant's latest option naming) and
         * falls back to the embedded payload from Liquid. Returns ''
         * when the product has no options at all.
         */
        const productFirstOptionName = (p) => {
          if (p?.handle) {
            const fromJson = productJson.value[p.handle];
            const jsonOpts = Array.isArray(fromJson?.options)
              ? fromJson.options
              : null;
            if (jsonOpts && jsonOpts.length) {
              return String(jsonOpts[0] || '').trim();
            }
          }
          const opts = Array.isArray(p?.options) ? p.options : [];
          return opts.length ? String(opts[0] || '').trim() : '';
        };

        /**
         * Whether to suppress the variant `<select>` for product `p`
         * in the product grid. Today only the "Size" filter is
         * supported (case-insensitive) and only when the merchant
         * opts in via the `hide_size_variant_selector` setting. The
         * image + control still commits the first-available variant via
         * `pickVariant`, so hiding the picker does not block the
         * customer from adding the item.
         */
        const shouldHideVariantSelector = (p) => {
          if (!hideSizeVariantSelector) return false;
          const first = productFirstOptionName(p).toLowerCase();
          return first === 'size';
        };

        /**
         * Same rule as `shouldHideVariantSelector` for the free-gift
         * `<select>`: hide when the first option is "Size" (case-insensitive)
         * and the section `hide_size_variant_selector` setting is on.
         * Default variant selection still applies via `resolveRewardVariantId`.
         */
        const shouldHideRewardGiftSelector = (row) => {
          if (!hideSizeVariantSelector) return false;
          const first = String(row?.optionName || '').trim().toLowerCase();
          return first === 'size';
        };

        const ensureProductJson = (handle) => {
          if (!handle || productJson.value[handle]) return;
          return fetch(H.productsJsonUrl(handle))
            .then((r) => (r.ok ? r.json() : null))
            .then((j) => {
              if (j) {
                productJson.value = { ...productJson.value, [handle]: j };
              }
            })
            .catch(() => {});
        };

        const persistBundleGroupId = (g) => {
          if (!g) return;
          stableBundleGroupId.value = g;
          try {
            localStorage.setItem(
              storageKey(parentVariantNumeric),
              JSON.stringify({ bundleGroupId: g })
            );
          } catch {
            /* noop */
          }
        };

        const clearBundleState = () => {
          hasBundleInCart.value = false;
          stableBundleGroupId.value = null;
          try {
            localStorage.removeItem(storageKey(parentVariantNumeric));
          } catch {
            /* noop */
          }
        };

        const syncFromCart = (cart) => {
          if (!cart?.items) {
            lines.value = [];
            clearBundleState();
            return;
          }
          const parentItem = H.findBundleParentLine(
            cart,
            parentVariantNumeric,
            { groupId: BUNDLE_GROUP_ID }
          );
          if (parentItem) {
            const fromIc = buildHydratedLinesFromItemComponents(
              parentItem,
              products
            );
            if (fromIc && fromIc.length) {
              hasBundleInCart.value = true;
              const g = H.getProp(parentItem, '_bundle_group_id');
              persistBundleGroupId(
                g && String(g).trim() !== '' ? g : BUNDLE_GROUP_ID
              );
              lines.value = fromIc;
              return;
            }
            const rawIds = H.getProp(
              parentItem,
              '_bundle_component_variant_ids'
            );
            const ids = H.parseBundleComponentVariantIds(rawIds);
            if (ids.length) {
              const qs = H.parseIntArrayFromJsonProperty(
                H.getProp(parentItem, '_bundle_component_quantities')
              );
              hasBundleInCart.value = true;
              const g = H.getProp(parentItem, '_bundle_group_id');
              persistBundleGroupId(
                g && String(g).trim() !== '' ? g : BUNDLE_GROUP_ID
              );
              lines.value = buildHydratedLinesFromParent(
                ids,
                qs,
                products,
                parentItem
              );
              return;
            }
          }
          const cg = H.getCanonicalBundleGroupId(cart, parentGid);
          if (!cg) {
            lines.value = [];
            clearBundleState();
            return;
          }
          hasBundleInCart.value = true;
          persistBundleGroupId(BUNDLE_GROUP_ID);
          const rawGroupLines = H.collectLinesForGroup(
            cart,
            parentGid,
            cg
          );
          lines.value = rawGroupLines.map((item) => {
            const q = Math.max(
              1,
              parseInt(String(item.quantity ?? 1), 10) || 1
            );
            return { ...item, quantity: q };
          });
        };

        const refresh = () => {
          loading.value = true;
          H.fetchCart()
            .then((c) => (c ? H.enforceSingleBundleGroup(c, parentGid) : null))
            .then((c) => {
              if (c) syncFromCart(c);
              else {
                lines.value = [];
                clearBundleState();
              }
            })
            .finally(() => {
              loading.value = false;
            });
        };

        watch(loading, (isLoading) => {
          if (isLoading) return;
          nextTick(() => {
            requestAnimationFrame(() => {
              syncMobileBarFromWindowScroll();
            });
          });
        });

        onMounted(() => {
          refresh();
          const mq = window.matchMedia(MOBILE_FLOATING_BAR_MQ);

          syncMobileBarFromWindowScroll();

          const onWinScroll = () => syncMobileBarFromWindowScroll();
          window.addEventListener('scroll', onWinScroll, {
            passive: true,
            capture: true,
          });

          const onMqChange = () => {
            syncMobileBarFromWindowScroll();
          };

          if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onMqChange);
          } else if (typeof mq.addListener === 'function') {
            mq.addListener(onMqChange);
          }

          nextTick(() => measureMobileFloatingBar());

          mobileRevealTeardown = () => {
            window.removeEventListener('scroll', onWinScroll, {
              capture: true,
            });
            if (typeof mq.removeEventListener === 'function') {
              mq.removeEventListener('change', onMqChange);
            } else if (typeof mq.removeListener === 'function') {
              mq.removeListener(onMqChange);
            }
          };
        });

        onUnmounted(() => {
          mobileRevealTeardown();
        });

        const lineCents = (line) => lineCentsRaw(line);
        const lineKey = (line) =>
          line.key || line._localId || String(line.id || '');

        /**
         * Normalize product id on a line, whether the line came from
         * `makeLocalLine` / `buildHydratedLinesFromParent`
         * (`product_id`) or directly from Shopify's `/cart.js` item
         * shape (sometimes exposed as `productId` in edge payloads.)
         */
        const lineProductId = (line) => {
          if (!line) return null;
          const v = line.product_id ?? line.productId;
          return v != null ? v : null;
        };

        const lineVariantId = (line) =>
          line?.variant_id ?? line?.variantId ?? null;

        /**
         * Units of this **grid card** in the set: same product id
         * and the variant currently selected in the card dropdown
         * (or implied single-variant). Matches YSE-style per-card qty.
         */
        const quantityInSetForGrid = (p) => {
          if (p == null || p.id == null) return 0;
          const vid = selectedVariantId(p);
          if (!vid) return 0;
          let n = 0;
          for (const l of lines.value) {
            if (String(lineProductId(l)) !== String(p.id)) continue;
            if (String(lineVariantId(l) || '') !== String(vid)) continue;
            n += getLineUnitCount(l);
          }
          return n;
        };

        /**
         * Remove one unit matching the card's selected variant,
         * walking from the last line backward so behavior matches
         * stacked `addToSet` calls (one local line per tap).
         */
        const removeOneFromSetForGrid = (p) => {
          if (actionKey.value) return;
          if (!p) return;
          const vid = selectedVariantId(p);
          if (!vid) return;
          for (let i = lines.value.length - 1; i >= 0; i -= 1) {
            const l = lines.value[i];
            if (String(lineProductId(l)) !== String(p.id)) continue;
            if (String(lineVariantId(l) || '') !== String(vid)) continue;
            const q = getLineUnitCount(l);
            if (q > 1) {
              const next = [...lines.value];
              next[i] = { ...l, quantity: q - 1 };
              lines.value = next;
            } else {
              lines.value = lines.value.filter((_, idx) => idx !== i);
            }
            return;
          }
        };

        const onVariantSelect = (e, p) => {
          const v = e?.target?.value;
          if (v) setVariantPick(p.id, v);
        };

        const selectedVariantId = (p) => {
          const list = productVariantList(p);
          if (!list.length) return '';
          const id = variantPick.value[p.id];
          if (id) {
            const has = list.some(
              (x) => String(x.id) === String(id)
            );
            if (has) return String(id);
          }
          const firstAvail = list.find((x) => x.available);
          const f = firstAvail || list[0];
          return f ? String(f.id) : '';
        };

        /**
         * Selected variant object for the product grid (follows the
         * same pick rules as `selectedVariantId`).
         */
        const gridVariantForProduct = (p) => {
          const list = productVariantList(p);
          if (!list.length) return null;
          const sid = selectedVariantId(p);
          if (!sid) return null;
          return list.find((x) => String(x.id) === String(sid)) || null;
        };

        const gridVariantDisplayTitle = (p) => {
          const v = gridVariantForProduct(p);
          if (!v?.title) return '';
          const t = String(v.title).trim();
          return t && t !== 'Default Title' ? t : '';
        };

        const gridVariantPriceCents = (p) => {
          const v = gridVariantForProduct(p);
          return v ? variantPriceToCents(v) : 0;
        };

        /**
         * Canonical storefront URL for a product handle, respecting
         * `Shopify.routes.root` so localized `/en-ca/` prefixes work.
         */
        const productPageUrl = (p) => {
          if (!p?.handle) return '#';
          const r = global.Shopify?.routes?.root;
          const base =
            r && typeof r === 'string' ? r.replace(/\/?$/, '') : '';
          return `${base}/products/${encodeURIComponent(String(p.handle))}`;
        };

        const optionLabel = (v) => {
          if (!v) return '';
          const title =
            v.title && String(v.title).trim() !== 'Default Title'
              ? String(v.title).trim()
              : 'Option';
          if (v.available) return title;
          return `${soldOutLabel} – ${title}`;
        };

        const displaySaleRowLabel = () => {
          const p = discountPercent.value;
          if (p > 0) {
            return `${p}% ${saleLabel}`;
          }
          return saleLabel;
        };

        const lineImageSrc = (line) => {
          if (line?._localId) {
            return line.image
              ? String(line.image)
              : '';
          }
          if (!line?.image) return '';
          if (typeof line.image === 'string') return line.image;
          if (line.image && typeof line.image === 'object' && line.image.src) {
            return String(line.image.src);
          }
          return '';
        };

        const addToSet = (p) => {
          if (actionKey.value) return;
          if (!p) return;
          const vars = productVariantList(p);
          if (!vars.length) {
            void ensureProductJson(p.handle).then(() => {
              const v2 = productVariantList(p);
              if (v2.length) addToSet(p);
            });
            return;
          }
          const v = pickVariant({ ...p, variants: vars }, variantPick.value);
          if (!v || !v.id) {
            global.alert('Choose an available option.');
            return;
          }
          if (!v.available) {
            global.alert(soldOutLabel);
            return;
          }
          const img =
            p.featured_image && String(p.featured_image).trim() !== ''
              ? p.featured_image
              : '';
          lines.value = [
            ...lines.value,
            makeLocalLine(
              p,
              v,
              img
            ),
          ];
        };

        /** Same enable rules as the “+” on the image; used for media click + a11y. */
        const cardMediaCanAdd = (p) =>
          Boolean(
            p &&
              !isAnyBusy() &&
              selectedVariantId(p) &&
              gridVariantForProduct(p)?.available
          );

        const cardMediaClickAdd = (p) => {
          if (!cardMediaCanAdd(p)) return;
          addToSet(p);
        };

        const cardMediaKeydownAdd = (e, p) => {
          if (!cardMediaCanAdd(p)) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            addToSet(p);
          }
        };

        const bundleComponentProperties = (slot, bundleGroupId) => ({
          _bundle_parent_variant_id: parentGid,
          _bundle_group_id: bundleGroupId,
          _bundle_kind: 'save',
          _bundle_role: 'component',
          _bundle_slot: String(slot),
        });

        /**
         * Properties for a tier-reward line.
         *
         * IMPORTANT: tier rewards live OUTSIDE the bundle on purpose.
         * They intentionally carry NO `_bundle_*` properties so that:
         *   - the bundle's collapse / discount Shopify Function
         *     does not include the gift in the bundle's item count
         *     (which is the metric the % discount keys on)
         *   - the cart transform that builds the parent line's
         *     `_bundle_component_variant_ids` does not list the
         *     reward GIDs (which previously leaked into the sidebar
         *     grid as "Item / 0.00" stub rows)
         *   - removing the bundle does not implicitly remove the
         *     gift via group-id matching; re-commit from the section
         *     uses `sweepBundleSetRewardsForThisSection`, and cart
         *     removals use `_bundle_set_parent_variant_id` +
         *     `removeOrphanBundleSetGifts` in bundle-cart-helpers.
         *
         * BXGY contract:
         *   - `_bxgy_auto: 'true'` -- match condition for the
         *     existing free-gift Shopify Function (100% off).
         *   - `_bxgy_source: 'bundle-set'` -- keeps the BXGY theme-
         *     auto-add reconciler from claiming this line (its
         *     `isAutoAddedLine` skips non-`theme-auto-add` sources).
         *   - `_bxgy_rule_id: 'bundle-set-<sectionId>-tier-<n>'` --
         *     stable id so this section's commit cycle can find and
         *     remove ITS rewards without touching gifts owned by
         *     another section or by BXGY itself.
         */
        const bundleRewardProperties = (tier) => ({
          _bxgy_auto: 'true',
          _bxgy_rule_id: `bundle-set-${sectionId}-tier-${tier.tierIndex}`,
          _bxgy_source: 'bundle-set',
          _bundle_set_parent_variant_id: String(parentVariantNumeric),
        });

        /**
         * Identify the rewards owned by this specific section's
         * commit cycle. We match on BOTH `_bxgy_source` and the
         * section-scoped `_bxgy_rule_id` prefix so that a page with
         * multiple bundle-set sections (or a customer cart that
         * survived a section-id change) doesn't accidentally clear
         * gifts owned by a sibling section.
         */
        const rewardRulePrefix = `bundle-set-${sectionId}-tier-`;
        const isThisSectionsRewardLine = (item) => {
          if (!item) return false;
          if (H.getProp(item, '_bxgy_source') !== 'bundle-set') return false;
          const rid = H.getProp(item, '_bxgy_rule_id') || '';
          return rid.indexOf(rewardRulePrefix) === 0;
        };

        /**
         * Sequentially remove this section's reward lines from the
         * cart. Returns a Promise that resolves when every reward
         * has been zeroed. Components are NOT touched here — the
         * existing `clearAllBundleComponentsForParent` helper owns
         * those (and intentionally only matches `_bundle_role:
         * 'component'`, so it now leaves rewards alone).
         */
        const sweepBundleSetRewardsForThisSection = (cart) => {
          if (!cart?.items?.length) return Promise.resolve();
          const keys = cart.items
            .filter(isThisSectionsRewardLine)
            .map((i) => i.key)
            .filter(Boolean);
          let chain = Promise.resolve();
          for (const key of keys) {
            chain = chain.then(() =>
              H.changeLineQuantityByKey(key, 0).then((res) => {
                if (!res.ok) {
                  return Promise.reject(
                    new Error('Could not remove a bundle reward line.')
                  );
                }
              })
            );
          }
          return chain;
        };

        /**
         * Fixed _bundle_group_id (default "bundle-and-save") for all component lines, configurable in section settings.
         */
        const resolveBundleGroupIdForSubmit = () => {
          persistBundleGroupId(BUNDLE_GROUP_ID);
          return BUNDLE_GROUP_ID;
        };

        const handleBundlePostError = (err) => {
          global.console.error(
            '[CustomBundleSet] bundle cart update failed',
            err
          );
          if (err?.message) global.alert(err.message);
          return H.fetchCart()
            .then((c) => (c ? syncFromCart(c) : null))
            .catch(() => {});
        };

        /**
         * Pushes the current `lines` to the server (re-add after clearing old bundle)
         * or removes all bundle lines when `lines` is empty.
         *
         * - If **every unit** in the set is the **same component variant**: one
         *   /cart/add.js line per unit (`quantity: 1`, `_bundle_slot` 1..N) so the
         *   bundle can treat each as its own slot.
         * - If there are **two or more distinct** component variants: merge
         *   identical variants into one add line with summed `quantity` and
         *   one `_bundle_slot` per unique variant (order = first time that variant
         *   appears when walking the set).
         */
        const commitBundleToCart = () => {
          if (!parentVariantNumeric || Number.isNaN(parentVariantNumeric)) {
            return Promise.reject(
              new Error('Bundle parent variant is not configured.')
            );
          }
          const bundleGroupId = resolveBundleGroupIdForSubmit();
          if (!lines.value.length) {
            return H.fetchCart()
              .then((c) => H.clearAllBundleComponentsForParent(c, parentGid))
              .then(() => H.fetchCart())
              .then((c) => sweepBundleSetRewardsForThisSection(c))
              .then(() => H.fetchCart())
              .then((c) =>
                H.clearBundleParentLineIfPresent(c, parentVariantNumeric)
              )
              .then(() => {
                H.afterCartUpdate();
                return H.fetchCart();
              })
              .then((c) => (c ? H.enforceSingleBundleGroup(c, parentGid) : c))
              .then((c) => {
                if (c) syncFromCart(c);
                else {
                  lines.value = [];
                  clearBundleState();
                }
              });
          }
          const unitVariantIds = [];
          for (const line of lines.value) {
            const vid = Number(line.variant_id);
            if (Number.isNaN(vid)) continue;
            const q = Math.max(
              1,
              parseInt(String(line.quantity || 1), 10) || 1
            );
            for (let u = 0; u < q; u += 1) {
              unitVariantIds.push(vid);
            }
          }
          const allSameComponentVariant =
            unitVariantIds.length > 0 &&
            unitVariantIds.every((id) => id === unitVariantIds[0]);

          let items;
          if (allSameComponentVariant) {
            const vid = unitVariantIds[0];
            items = unitVariantIds.map((_, i) => ({
              id: vid,
              quantity: 1,
              properties: bundleComponentProperties(
                i + 1,
                bundleGroupId
              ),
            }));
          } else {
            const mergeOrder = [];
            const sumQty = new Map();
            for (const line of lines.value) {
              const vid = Number(line.variant_id);
              if (Number.isNaN(vid)) continue;
              const q = Math.max(
                1,
                parseInt(String(line.quantity || 1), 10) || 1
              );
              if (sumQty.has(vid)) {
                sumQty.set(vid, sumQty.get(vid) + q);
              } else {
                mergeOrder.push(vid);
                sumQty.set(vid, q);
              }
            }
            items = mergeOrder.map((vid, i) => ({
              id: vid,
              quantity: sumQty.get(vid),
              properties: bundleComponentProperties(
                i + 1,
                bundleGroupId
              ),
            }));
          }

          // Append one reward line per qualifying tier (gifts stack).
          // Quantity is hard-pinned to 1 — the discount Function is
          // expected to discount exactly one unit per gift line.
          // Component count for tier qualification is the unit count
          // (same metric used for `discountPercent` above) so a
          // 4-unit set with a single line still qualifies for tier 3.
          //
          // The reward items live OUTSIDE the bundle group (no
          // `_bundle_*` properties) so they don't get rolled into
          // the bundle's item count or % discount calculation. They
          // ride along in the same /cart/add.js call purely so the
          // cart end-state is atomic — Shopify accepts heterogeneous
          // items in a single payload.
          const totalUnits = unitVariantIds.length;
          const rewardItems = [];
          for (const tier of rewardTiers) {
            if (totalUnits < tier.min) continue;
            const variantId = resolveRewardVariantId(tier, sectionId);
            if (!variantId) continue;
            rewardItems.push({
              id: variantId,
              quantity: 1,
              properties: bundleRewardProperties(tier),
            });
          }
          if (rewardItems.length) {
            items = items.concat(rewardItems);
          }
          try {
            if (global.localStorage.getItem('debugCustomBundleSet') === '1') {
              global.console.log('[CustomBundleSet] cart/add items', {
                allSameComponentVariant,
                componentCount: items.length - rewardItems.length,
                rewardCount: rewardItems.length,
                items: JSON.parse(JSON.stringify(items)),
              });
            }
          } catch {
            /* noop */
          }
          return H.fetchCart()
            .then((c) => H.clearAllBundleComponentsForParent(c, parentGid))
            .then(() => H.fetchCart())
            .then((c) => sweepBundleSetRewardsForThisSection(c))
            .then(() => H.fetchCart())
            .then((c) => H.clearBundleParentLineIfPresent(c, parentVariantNumeric))
            .then(() =>
              fetch(addUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items }),
              })
            )
            .then(H.parseAddResponse)
            .then(() => {
              H.afterCartUpdate();
              return H.fetchCart();
            })
            .then((c) => (c ? H.enforceSingleBundleGroup(c, parentGid) : c))
            .then((c) => {
              if (c) syncFromCart(c);
            });
        };

        /**
         * Single sync point with the cart. Both adds AND removes
         * accumulate locally on `lines.value`; this CTA handler is
         * what actually pushes those changes to /cart/add.js. An
         * empty local set with a bundle in cart is a valid commit
         * shape — `commitBundleToCart` clears the bundle when
         * `lines.value.length === 0`.
         */
        const addBundleToCart = () => {
          if (actionKey.value) return;
          if (!canAddBundle.value) return;
          if (!parentVariantNumeric || Number.isNaN(parentVariantNumeric)) {
            global.alert('Bundle parent variant is not configured.');
            return;
          }
          actionKey.value = 'post-bundle';
          commitBundleToCart()
            .catch(handleBundlePostError)
            .finally(() => {
              actionKey.value = null;
            });
        };

        /**
         * Match `target` against a line in `lines.value` using the
         * most stable identifier the line has. `_localId` wins (set
         * for both freshly-added and parent-hydrated lines), then a
         * server `key` (raw cart line, no parent collapse), and
         * finally object identity as a last resort.
         */
        const matchesSameLine = (candidate, target) => {
          if (target._localId) return candidate._localId === target._localId;
          if (target.key) return candidate.key === target.key;
          return candidate === target;
        };

        /**
         * Remove a line from the LOCAL set only. The cart is NOT
         * updated until the customer clicks "Update the set" — this
         * mirrors the additive UX (adding items also stays local
         * until commit) so customers can stack add/remove edits and
         * pay for them in a single cart round-trip.
         */
        const removeFromSet = (line) => {
          if (actionKey.value) return;
          if (!line) return;
          lines.value = lines.value.filter(
            (l) => !matchesSameLine(l, line)
          );
        };

        /**
         * Slot-grid "Remove" click. Decrements the line's quantity
         * by one when it represents multiple units (same variant ×
         * N), otherwise drops the line entirely. All mutations are
         * local — see `removeFromSet`.
         */
        const removeDisplaySlotAt = (slot) => {
          if (actionKey.value) return;
          const row = lineForSlot(slot);
          if (!row) return;
          const line = row.line;
          const q = getLineUnitCount(line);
          if (q > 1) {
            const i = lines.value.findIndex((l) => matchesSameLine(l, line));
            if (i < 0) return;
            const next = [...lines.value];
            next[i] = { ...line, quantity: q - 1 };
            lines.value = next;
            return;
          }
          lines.value = lines.value.filter(
            (l) => !matchesSameLine(l, line)
          );
        };

        const isAnyBusy = () => actionKey.value != null;

        const lineForSlot = (slot) => {
          if (!slot || slot.kind !== 'product') return null;
          const rows = sidebarDisplayRows.value;
          const idx = slot.productIndex;
          if (idx < 0 || idx >= rows.length) return null;
          return rows[idx] || null;
        };

        const lineCentsForSidebarIndex = (slot) => {
          const row = lineForSlot(slot);
          if (!row) return 0;
          return lineCentsPerUnit(row.line);
        };

        const slotKeyAt = (slot) => {
          if (!slot) return 'empty';
          if (slot.kind === 'gift') return slot.key;
          const row = lineForSlot(slot);
          return row ? row.key : slot.key;
        };

        const slotDisplayNumber = (slot) => {
          if (!slot || slot.kind !== 'product') return '';
          return slot.displayNumber;
        };

        /**
         * Highest percent-off gate the current unit count has unlocked.
         * Used so only the *current* discount overlay stays visible —
         * reaching 25% hides the 10% border + badge, etc.
         */
        const activePercentGate = computed(() => {
          const count = totalUnitCount.value;
          let active = null;
          for (const g of sortedDiscountGates.value) {
            if (count >= g.min) active = g;
          }
          return active;
        });

        /**
         * Percent-off overlay for a numbered product slot.
         * Shown while locked (dashed accent) and after unlock (solid
         * brand blue), until a later percent tier takes over.
         */
        const percentMilestoneForSlot = (slot) => {
          if (!slot || slot.kind !== 'product') return null;
          const min = Number(slot.displayNumber);
          if (!min) return null;
          const gate = sortedDiscountGates.value.find((g) => g.min === min);
          if (!gate) return null;
          const reached = totalUnitCount.value >= gate.min;
          const active = activePercentGate.value;
          if (reached && active && active.min !== gate.min) return null;
          return {
            reached,
            label: reached
              ? `${gate.percent}% OFF`
              : `Unlock ${gate.percent}% OFF`,
            compactLabel: `${gate.percent}% OFF`,
          };
        };

        const milestoneForEmptySlot = (slot) => {
          const gift = giftRewardForSlot(slot);
          if (gift) {
            return gift.unlocked ? 'Free gift' : 'Unlock a free gift';
          }
          const info = percentMilestoneForSlot(slot);
          return info ? info.label : '';
        };

        /**
         * Short milestone label rendered in the mobile collapsed
         * slot strip. Same lookup as `milestoneForEmptySlot` but
         * uses the compact label map so the badge fits below a
         * ~3.5rem-wide tile.
         */
        const compactMilestoneForEmptySlot = (slot) => {
          const gift = giftRewardForSlot(slot);
          if (gift) {
            return gift.unlocked ? 'Added' : 'Free gift';
          }
          const info = percentMilestoneForSlot(slot);
          return info ? info.compactLabel : '';
        };

        const isPercentMilestoneReached = (slot) => {
          const gift = giftRewardForSlot(slot);
          if (gift) return !!gift.unlocked;
          const info = percentMilestoneForSlot(slot);
          return !!(info && info.reached);
        };

        /**
         * Sidebar `<select>` change handler for multi-variant
         * rewards. Updates the local pick map immediately so the row
         * re-renders, persists to localStorage so the choice survives
         * a reload, and (when the bundle is already in cart) re-runs
         * `commitBundleToCart` so the live cart line gets swapped to
         * the new variant.
         */
        const onRewardVariantSelect = (e, tierIndex) => {
          const raw = e?.target?.value;
          const newId = parseInt(raw, 10) || null;
          if (!newId) return;
          rewardVariantPicks.value = {
            ...rewardVariantPicks.value,
            [tierIndex]: newId,
          };
          writePickedRewardVariantId(sectionId, tierIndex, newId);
          if (
            hasBundleInCart.value &&
            lines.value.length &&
            !actionKey.value
          ) {
            actionKey.value = 'reward-swap';
            commitBundleToCart()
              .catch(handleBundlePostError)
              .finally(() => {
                actionKey.value = null;
              });
          }
        };

        const rewardVariantOptionLabel = (variant) => {
          if (!variant) return '';
          const titleFirst = String(variant.title || '').split('/')[0].trim();
          if (variant.available) return titleFirst || 'Option';
          return `${soldOutLabel} – ${titleFirst}`;
        };

        /**
         * Single-line gift copy per tier: locked shows the threshold;
         * unlocked confirms the reward + tier percent.
         */
        const rewardGiftBodySentence = (row) => {
          if (!row) return '';
          const title = String(row.productTitle || '').trim() || 'Free gift';
          const vt = String(row.displayVariantTitle || '').trim();
          const variantBit =
            vt && vt.toLowerCase() !== 'default title' ? ` (${vt})` : '';
          const n = Math.max(1, Number(row.min) || 1);
          const itemsWord = n === 1 ? 'item' : 'items';
          if (row.unlocked) {
            const pct = Math.min(100, Math.max(0, Number(row.percent) || 0));
            return `✓ Your free ${title} is in your bundle.`;
          }
          return `Unlock a ${title} when you add ${n} ${itemsWord}.`;
        };

        return {
          minLineItems,
          showTierProgressHint,
          tierProgressHintText,
          bundleCtaLabel,
          isStartState,
          hasBundleInCart,
          products,
          lines,
          loading,
          lineCount,
          canAddBundle,
          sumCents,
          totalDiscountedCents,
          discountPercent,
          hasDiscount,
          soldOutLabel,
          addProductToBuilderLabel,
          addToSetCtaLabel,
          updateSetCtaLabel,
          removeFromSetLabel,
          emptySetLabel,
          subtotalLabel,
          saleLabel,
          variantPick,
          setVariantPick,
          productVariantList,
          shouldHideVariantSelector,
          shouldHideRewardGiftSelector,
          ensureProductJson,
          onVariantSelect,
          optionLabel,
          displaySaleRowLabel,
          selectedVariantId,
          quantityInSetForGrid,
          removeOneFromSetForGrid,
          gridVariantDisplayTitle,
          gridVariantForProduct,
          gridVariantPriceCents,
          productPageUrl,
          addToSet,
          cardMediaCanAdd,
          cardMediaClickAdd,
          cardMediaKeydownAdd,
          addBundleToCart,
          removeFromSet,
          removeDisplaySlotAt,
          isAnyBusy,
          lineCents,
          lineKey,
          lineImageSrc,
          lineForSlot,
          lineCentsForSidebarIndex,
          slotKeyAt,
          slotDisplayNumber,
          displaySlots,
          milestoneForEmptySlot,
          compactMilestoneForEmptySlot,
          isPercentMilestoneReached,
          sidebarSlotCount,
          selectSlotLabel,
          sidebarPromoText,
          sidebarPromoImage,
          showSidebarPromo,
          rewardTierRows,
          hasRewardTiers,
          isGiftSlot,
          giftRewardForSlot,
          onRewardVariantSelect,
          rewardVariantOptionLabel,
          rewardGiftBodySentence,
          mobilePanelOpen,
          toggleMobilePanel,
          mobileBarRevealed,
          mobileChosenLabel,
          formatMoney: (c) => H.formatMoney(c),
        };
      },
    });

    app.mount(rootEl);

    if (sectionEl) {
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => measureMobileFloatingBar());
        const startObserving = () => {
          const aside = sectionEl.querySelector('.custom-bundle-set__side');
          if (aside) {
            ro.observe(aside);
            measureMobileFloatingBar();
          } else {
            window.requestAnimationFrame(startObserving);
          }
        };
        startObserving();
      } else {
        window.requestAnimationFrame(measureMobileFloatingBar);
      }
      window.addEventListener('resize', measureMobileFloatingBar, {
        passive: true,
      });
    }
  };
})(typeof window !== 'undefined' ? window : this);
